/**
 * useSphereSurfaces.ts
 *
 * Hook for fetching the resolved default Space, default context, and per-host
 * compositions for a sphere + viewer, from the server-side surface-bindings
 * resolver (GET /spheres/:sphereId/surfaces).
 *
 * This hook is render-only: it answers no authorization question and carries
 * no SpiceDB namespace / permission strings. The server still gates content
 * behind `sphere#view`; a null/failed response here is a *surface* fallback
 * (open the default Space instead), not an authorization bypass.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';

export interface ResolvedDefaultSpace {
  systemName: string;
  metaspaceId?: string;
}

export interface SurfaceCompositionRef {
  systemName: string;
  version?: number;
}

export interface ResolvedSurfaceContext {
  systemName: string;
  source: string;
}

/** Data returned by GET /spheres/:sphereId/surfaces */
export interface SphereSurfacesData {
  defaultContext: ResolvedSurfaceContext | null;
  defaultSpace: ResolvedDefaultSpace | null;
  spaceOrder: string[];
  compositions: Record<string, SurfaceCompositionRef | null>;
  arc: { flowId: string; currentStageId: string } | null;
  resolvedFrom?: Record<string, unknown>;
}

/** Return value of the useSphereSurfaces hook */
export interface UseSphereSurfacesReturn {
  defaultContext: ResolvedSurfaceContext | null;
  defaultSpace: ResolvedDefaultSpace | null;
  spaceOrder: string[];
  compositions: Record<string, SurfaceCompositionRef | null>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_COMPOSITIONS: Record<string, SurfaceCompositionRef | null> = {};

/**
 * Fetches the resolved surfaces (default Space, default context, compositions)
 * for a sphere based on its deployed approaches' surface bindings.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param sphereId - Sphere to fetch resolved surfaces for (null/undefined skips fetch)
 */
export function useSphereSurfaces(
  apiClients: ApiClients | null,
  sphereId: string | null | undefined
): UseSphereSurfacesReturn {
  const [defaultContext, setDefaultContext] = useState<ResolvedSurfaceContext | null>(null);
  const [defaultSpace, setDefaultSpace] = useState<ResolvedDefaultSpace | null>(null);
  const [spaceOrder, setSpaceOrder] = useState<string[]>([]);
  const [compositions, setCompositions] =
    useState<Record<string, SurfaceCompositionRef | null>>(EMPTY_COMPOSITIONS);
  const [error, setError] = useState<string | null>(null);

  // `loading` is DERIVED, never a plain `useState(false)`. Consumers (the
  // redirectors) navigate to a fallback Space the moment they observe
  // `!loading` with no `defaultSpace`. A boolean initialised to `false` is
  // "settled with no result" on the very first render — and on every render
  // where `apiClients` transitions from null to ready — so the fallback fires
  // before the fetch effect has even run and the resolved Space never wins.
  // Keying "settled" to the inputs that produced it makes an unfetched
  // (sphereId, apiClients) pair unambiguously *not* settled.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const requestKey = apiClients?.graphClient && sphereId ? sphereId : null;
  const loading = refreshing || (requestKey !== null && settledKey !== requestKey);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchSurfaces = useCallback(
    async (skipCache = false) => {
      if (!apiClients?.graphClient || !sphereId) return;

      setRefreshing(true);
      setError(null);

      try {
        const params: Record<string, string> = {};
        if (skipCache) {
          params.skipCache = 'true';
        }
        const data = await apiClients.graphClient.get<SphereSurfacesData>(
          `/spheres/${sphereId}/surfaces`,
          { params }
        );

        if (isMountedRef.current) {
          setDefaultContext(data.defaultContext ?? null);
          setDefaultSpace(data.defaultSpace ?? null);
          setSpaceOrder(data.spaceOrder ?? []);
          setCompositions(data.compositions ?? EMPTY_COMPOSITIONS);
        }
      } catch (err: unknown) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch sphere surfaces');
          setDefaultContext(null);
          setDefaultSpace(null);
          setSpaceOrder([]);
          setCompositions(EMPTY_COMPOSITIONS);
        }
      } finally {
        if (isMountedRef.current) {
          setRefreshing(false);
          setSettledKey(sphereId);
        }
      }
    },
    [apiClients, sphereId]
  );

  // Fetch on mount and when sphereId changes
  useEffect(() => {
    if (!apiClients || !sphereId) {
      setDefaultContext(null);
      setDefaultSpace(null);
      setSpaceOrder([]);
      setCompositions(EMPTY_COMPOSITIONS);
      setSettledKey(null);
      return;
    }

    fetchSurfaces();
  }, [apiClients, sphereId, fetchSurfaces]);

  const refetch = useCallback(() => {
    fetchSurfaces(true);
  }, [fetchSurfaces]);

  return { defaultContext, defaultSpace, spaceOrder, compositions, loading, error, refetch };
}
